"use client"

import * as React from "react"
import { DayButton, type DayButtonProps, DayPicker } from "react-day-picker"
import { cs } from "date-fns/locale"
import { parseISO, isSameDay, startOfMonth, endOfMonth } from "date-fns"
import { motion } from "framer-motion"
import type { DailyEntry as StorageDailyEntry } from "@/lib/storage"
import type { DailyEntry as DBDailyEntry } from "@/lib/db/schema"
import { getZoneById } from "@/lib/zones"
import { formatCzechMonthYear, formatCzechDateWithYear } from "@/lib/date-utils"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { AdaptiveCalendarView } from "@/components/adaptive-calendar-view"
import { CalendarErrorBoundary } from "@/components/calendar-error-boundary"

// Support both old storage format and new DB format
type DailyEntry = StorageDailyEntry | DBDailyEntry

interface CalendarHistoryViewProps {
  entries: DailyEntry[]
  currentStreak?: number
  todayEntry?: DBDailyEntry
  onDateSelect?: (date: Date | undefined) => void
  useNewCalendar?: boolean // Feature flag for gradual rollout
}

export function CalendarHistoryView({ entries, currentStreak, todayEntry, onDateSelect, useNewCalendar }: CalendarHistoryViewProps) {
  const [shouldUseLegacy, setShouldUseLegacy] = React.useState(false)

  // Check environment variable for feature flag
  const envUseNewCalendar = process.env.NEXT_PUBLIC_USE_NEW_CALENDAR !== "false"

  // Final decision: respect prop override, otherwise use env flag (default true)
  const shouldUseNewCalendar = useNewCalendar !== undefined ? useNewCalendar : envUseNewCalendar

  // Use new calendar by default (can be controlled via feature flag)
  // But fallback to legacy if new calendar fails
  if (shouldUseNewCalendar && !shouldUseLegacy) {
    return (
      <CalendarErrorBoundary
        onReset={() => setShouldUseLegacy(true)}
        fallback={<LegacyCalendarView entries={entries} onDateSelect={onDateSelect} />}
      >
        <NewCalendarView entries={entries as DBDailyEntry[]} currentStreak={currentStreak} todayEntry={todayEntry} onDateSelect={onDateSelect} />
      </CalendarErrorBoundary>
    )
  }

  // Fallback to old calendar
  return <LegacyCalendarView entries={entries} onDateSelect={onDateSelect} />
}

/**
 * New calendar implementation using AdaptiveCalendarView
 */
function NewCalendarView({ entries, currentStreak, todayEntry, onDateSelect }: { entries: DBDailyEntry[], currentStreak?: number, todayEntry?: DBDailyEntry, onDateSelect?: (date: Date | undefined) => void }) {
  return (
    <div className="w-full">
      <AdaptiveCalendarView entries={entries} currentStreak={currentStreak} todayEntry={todayEntry} />
    </div>
  )
}

/**
 * Legacy calendar implementation - kept for backward compatibility
 */
function LegacyCalendarView({ entries, onDateSelect }: CalendarHistoryViewProps) {
  const [selectedDate, setSelectedDate] = React.useState<Date>()
  const [currentMonth, setCurrentMonth] = React.useState(new Date())

  // Create a map of entries for fast lookup
  const entriesMap = React.useMemo(() => {
    const map = new Map<string, DailyEntry>()
    entries.forEach((entry) => {
      map.set(entry.date, entry)
    })
    return map
  }, [entries])

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date)
    onDateSelect?.(date)
  }

  // Find entries with data for highlighting
  const daysWithEntries = React.useMemo(() => {
    return entries.map((entry) => parseISO(entry.date))
  }, [entries])

  return (
    <div className="w-full">
      <DayPicker
        mode="single"
        locale={cs}
        selected={selectedDate}
        onSelect={handleDateSelect}
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        numberOfMonths={1}
        className="mx-auto w-full"
        classNames={{
          months: "flex flex-col sm:flex-row gap-y-4 sm:gap-x-4 sm:gap-y-0",
          month: "w-full",
          month_caption: "flex justify-center pt-1 relative items-center mb-4",
          caption_label: "text-base font-semibold",
          nav: "flex items-center gap-1 absolute inset-x-0",
          button_previous: "absolute left-0",
          button_next: "absolute right-0",
          weekdays: "flex",
          weekday: "text-muted-foreground rounded-md w-9 sm:w-12 font-normal text-[0.8rem]",
          week: "flex w-full mt-2",
          day: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
          outside: "text-muted-foreground/30",
        }}
        modifiers={{
          hasEntry: daysWithEntries,
        }}
        components={{
          DayButton: (props: DayButtonProps) => (
            <ZoneDayButton {...props} entriesMap={entriesMap} />
          ),
        }}
        formatters={{
          formatCaption: (date) => formatCzechMonthYear(date),
        }}
      />

      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 rounded-lg bg-muted/50 border"
        >
          <div className="text-sm font-medium mb-2">{formatCzechDateWithYear(selectedDate)}</div>
          {(() => {
            const dateStr = selectedDate.toISOString().split("T")[0]
            const entry = entriesMap.get(dateStr)
            if (entry) {
              const zone = getZoneById(entry.zoneId)
              return (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{zone?.emoji}</span>
                    <span className="font-medium">{zone?.name}</span>
                  </div>
                  {entry.note && (
                    <div className="text-sm text-muted-foreground text-pretty">{entry.note}</div>
                  )}
                </div>
              )
            }
            return <div className="text-sm text-muted-foreground">Žádný záznam pro tento den</div>
          })()}
        </motion.div>
      )}
    </div>
  )
}

interface ZoneDayButtonProps extends DayButtonProps {
  entriesMap: Map<string, DailyEntry>
}

function ZoneDayButton({ day, modifiers, className, entriesMap, ...props }: ZoneDayButtonProps) {
  const dateStr = day.date.toISOString().split("T")[0]
  const entry = entriesMap.get(dateStr)
  const zone = entry ? getZoneById(entry.zoneId) : null
  const hasEntry = !!entry

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        "h-9 w-9 sm:h-12 sm:w-12 p-0 font-normal relative group transition-all",
        "hover:scale-110 active:scale-95",
        hasEntry && "font-semibold",
        modifiers.selected && "ring-2 ring-primary ring-offset-2",
        modifiers.today && "border-2 border-primary",
        modifiers.outside && "text-muted-foreground/30 opacity-50",
        className
      )}
      style={{
        backgroundColor: zone?.color || "transparent",
        opacity: hasEntry ? 1 : modifiers.outside ? 0.3 : 0.5,
      }}
      {...props}
    >
      <div className="flex flex-col items-center justify-center gap-0.5">
        <span className={cn("text-xs sm:text-sm", zone && "text-foreground")}>
          {day.date.getDate()}
        </span>
        {zone && <span className="text-sm sm:text-base leading-none">{zone.emoji}</span>}
      </div>

      {/* Tooltip on hover */}
      {hasEntry && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
          <div className="bg-popover text-popover-foreground text-xs rounded-lg shadow-lg px-3 py-2 border max-w-[200px] whitespace-nowrap">
            <div className="font-medium">{zone?.name}</div>
            {entry?.note && (
              <div className="text-muted-foreground mt-1 pt-1 border-t whitespace-normal text-pretty">
                {entry.note}
              </div>
            )}
          </div>
        </div>
      )}
    </Button>
  )
}
